import {
    Box,
    Button, Card, CardActions, CardContent, CardMedia, CircularProgress,
    Stack,
    TextField,
    IconButton,
    InputAdornment
} from "@mui/material";
import { useAuth } from "../../store/auth-context";
import {useEffect, useRef, useState} from "react";
import Typography from "@mui/material/Typography";
import ErrorIcon from '@mui/icons-material/Error';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import CheckIcon from '@mui/icons-material/Check';
import {GoogleLogin, GoogleOAuthProvider} from "@react-oauth/google";
import config from "../../config";
import GoogleIcon from '@mui/icons-material/Google';
import AppOnly from "../../components/platforms/AppOnly";
import WebOnly from "../../components/platforms/WebOnly";
import ForgotPasswordModal from "./ForgotPasswordModal";

const Login = (props) => {
    const emailElement = useRef('');
    const passwordElement = useRef('');

    const [currentStatus, setCurrentStatus] = useState(props.status);

    const [emailIsValid, setEmailIsValid] = useState(true);
    const [passwordIsValid, setPasswordIsValid] = useState(true);

    const [error, setError] = useState(false);
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [isForgotPasswordModalOpen, setIsForgotPasswordModalOpen] = useState(false);
    const googleLoginConfigured = config.HAS_GOOGLE_OAUTH_CLIENT_ID;

    const checkFormValidity = () => {
        return checkElementValidity('email') && checkElementValidity('password');
    }

    let message = null;
    switch (currentStatus){
        case 'verification_pending':
            message = <>
                <ErrorIcon color='error' fontSize="large" />
                <Typography variant='h6' color='error' textAlign='left' component="div">
                    Per accedere a Sbobbi devi verificare la tua email. Se non hai più il link di verifica procedi con una nuova registrazione usando la stessa email
                </Typography>
            </>;
            break;
        case 'verification_sent':
            message = <>
                <CheckIcon fontSize='large' color='success'  />
                <Typography color="success" variant="h6" textAlign='left' component="div">
                    Abbiamo inviato una mail al tuo indirizzo. Verifica il tuo account aprendo il link al suo interno.
                </Typography>
            </>
            break;
        case 'token_expired':
            message = <>
                <ErrorIcon color='error' fontSize="large" />
                <Typography variant='h6' color='error'>
                    Link di verifica scaduto, procedi con una nuova registrazione
                </Typography>
            </>;
            break;
        case 'token_not_valid':
            message = <>
                <ErrorIcon color='error' fontSize="large" />
                <Typography variant='h6' color='error' textAlign='left' component="div">
                    Token non valido, procedi con una nuova registrazione
                </Typography>
            </>;
            break;
        case 'token_verified':
            message = <>
                <CheckIcon fontSize='large' color='success'  />
                <Typography color="success" variant="h6" textAlign='left' component="div">
                    La tua email è stata verificata, puoi procedere con il login.
                </Typography>
            </>;
            break;
    }

    const authCtx = useAuth();

    useEffect(() => {
        if (authCtx.response && authCtx.response.request && authCtx.response.request.requestType === 'login') {
            console.log(authCtx.response)
            setLoading(false);
            if (authCtx.response.state !== "success"){
                if (authCtx.response.error?.message === 'Google login is not configured') {
                    setError('Google login is not configured');
                    return;
                }
                if (authCtx.response.error.code === 403){
                    return setCurrentStatus('verification_pending');
                }
                setError(true);
            }
        }
    }, [authCtx.response]);

    const checkElementValidity = element => {
        let pattern = null;
        let isValid = true;
        switch (element) {
            case 'email':
                pattern = /^\S+@\S+\.\S+$/;
                if (pattern.test(emailElement.current.value.trim())) {
                    setEmailIsValid(true);
                } else {
                    isValid = false;
                    setEmailIsValid(false);
                }
                break;
            case 'password':
                if (passwordElement.current.value.trim() !== "") {
                    setPasswordIsValid(true);
                } else {
                    isValid = false;
                    setPasswordIsValid(false);
                }
                break;
        }
        return isValid;
    }

    const onSubmitHandler = (ev) => {
        setError(false);
        ev.preventDefault();
        if (checkFormValidity()) {
            setLoading(true);
            authCtx.login(emailElement.current.value.trim(), passwordElement.current.value.trim());
        }
    }

    const onGoogleLoginBtnCLick = () => {
        authCtx.googleMobileLogin();
    }

    const containerStyle = {
        py:5,
        pb:3,
        boxSizing: { xs: 'border-box', sm: 'initial' },
        px: { xs: 1, sm: 4 },
        height: { xs: '100dvh', sm: 'auto' },
        width: { xs: '100dvw', sm: 'auto' },
        minWidth: { sm :400 },
        maxWidth: { sm :'' },
        position: { sm :'absolute' },
        left: { sm :'50%' },
        top: { sm :'50%' },
        transform: { sm :'translate(-50%, -50%)' },
    }

    return <>
        <form action='' onSubmit={onSubmitHandler}>
            <Card sx={containerStyle}>
                <CardMedia
                    sx={{borderRadius: 1, mx: 'auto', p: '25px', width: 'auto'}}
                    component="img"
                    alt="Logo Sbobbi"
                    height="150"
                    image="/logo.svg"
                />
                <CardContent sx={{ px: {xs: 0, sm: 2}, py: 2, textAlign: 'center'}}>
                    {message && <Box display='flex' mt={-2} mb={5} alignItems='center' gap={2} justifyContent='center'>
                        {message}
                    </Box>}
                    <Box sx={{ mb: 2}}>
                        <TextField
                            id="email-input"
                            error={ !emailIsValid }
                            helperText={ emailIsValid ? "" : "Email non valida!" }
                            label="Email"
                            variant="outlined"
                            autoComplete="email"
                            inputRef={emailElement}
                            onBlur={checkElementValidity.bind(this, "email")}
                            fullWidth={true}
                        />
                    </Box>
                    <Box>
                        <TextField
                            id="password-input"
                            error={ !passwordIsValid }
                            helperText={ passwordIsValid ? "" : "Inserire una password!" }
                            label="Password"
                            type={showPassword ? "text" : "password"}
                            variant="outlined"
                            autoComplete="current-password"
                            inputRef={passwordElement}
                            onBlur={checkElementValidity.bind(this, "password")}
                            fullWidth={true}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            aria-label="toggle password visibility"
                                            onClick={() => setShowPassword(!showPassword)}
                                            edge="end"
                                        >
                                            {showPassword ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>
                                )
                            }}
                        />
                    </Box>
                    <Box display="flex" justifyContent="flex-end" mt={1.5}>
                        <Typography
                            component="button"
                            type="button"
                            onClick={() => setIsForgotPasswordModalOpen(true)}
                            sx={{
                                border: 0,
                                background: 'transparent',
                                color: '#7b4fb1',
                                cursor: 'pointer',
                                fontSize: 15,
                                p: 0,
                                textDecoration: 'none',
                                '&:hover': {
                                    textDecoration: 'underline'
                                }
                            }}
                        >
                            Hai dimenticato la password?
                        </Typography>
                    </Box>
                </CardContent>
                <CardActions sx={{p: 2, justifyContent: 'center', flexDirection: 'column'}}>
                    {error && <Stack direction="row" mb={5} alignItems="center" spacing={3}>
                        <ErrorIcon color='error' fontSize="large"/>
                        <Typography
                            variant="body1"
                            component="div"
                            color="error">
                            {error === 'Google login is not configured'
                                ? 'Accesso con Google non configurato. Inserisci un Google Web Client ID valido nelle variabili ambiente.'
                                : 'Username o Password errata! Riprova...'}
                        </Typography>
                    </Stack>}
                    <Box textAlign="center">
                        {!loading && <Button sx={{height: 50, fontSize: 18}} type='submit' size='large' variant='contained' fullWidth={true} >Accedi</Button>}
                        {loading && <CircularProgress />}
                    </Box>

                    <Typography sx={{mt: 3, mb: 1, fontSize: 16}} variant='h6'>Oppure:</Typography>
                    <Box>
                        <AppOnly>
                            <Button startIcon={<GoogleIcon />} color={'error'} variant='contained' fullWidth onClick={onGoogleLoginBtnCLick} disabled={!googleLoginConfigured}>Accedi con Google</Button>
                        </AppOnly>
                        <WebOnly>
                            {googleLoginConfigured ? (
                                <GoogleOAuthProvider clientId={config.GOOGLE_OAUTH_CLIENT_ID}>
                                    <GoogleLogin
                                        onSuccess={credentialResponse => {
                                            authCtx.googleAuth(credentialResponse)
                                        }}
                                        onError={() => console.log('Login Failed')}
                                    />
                                </GoogleOAuthProvider>
                            ) : (
                                <Typography color="error" textAlign="center" sx={{ maxWidth: 320 }}>
                                    Accesso con Google non disponibile: manca un Google Web Client ID valido nella configurazione frontend.
                                </Typography>
                            )}
                        </WebOnly>
                    </Box>
                    <Typography mt={3} variant='body1'>
                        Non hai un account? <a href="#" onClick={props.onModeChange}>Registrati adesso!</a>
                    </Typography>
                    <Typography mt={3} fontSize={16} color="#376dab" variant='body1'>
                        by Bug Srl
                    </Typography>
                </CardActions>
            </Card>
        </form>
        <ForgotPasswordModal
            open={isForgotPasswordModalOpen}
            onClose={() => setIsForgotPasswordModalOpen(false)}
        />
    </>
};

export default Login;
